"use client";

import { c as _c } from "react/compiler-runtime";
import { WalletListSelector } from "@/app/(v2)/_components/WalletListSelector";
import { ImportPrivateKeyModal } from "@/app/(v2)/wallet-management/_components/ImportPrivateKeyModal";
import { useOrganizationWallets } from "@/hooks/useOrganizationWallets";
import { useTurnkeyWallets } from "@/hooks/useTurnkeyWallets";
import { B3OS_ORG_COOKIE_NAME } from "@/lib/authenticatedFetch";
import { useWalletsStore } from "@/stores/walletsStore";
import Cookies from "js-cookie";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRequiredTokens } from "./form-fields/RequiredTokensContext";
import type { ChainFamily } from "@/lib/utils/chain-family";
import { getChainFamily } from "@/lib/utils/chain-family";
import { ACCOUNT_TYPE_POLYMARKET_DEPOSIT_WALLET } from "@/lib/constants/wallet";
import { PolymarketDepositWalletSetupModal } from "./PolymarketDepositWalletSetupModal";
import { ConnectorLogo } from "@/app/(v2)/connectors/_components/ConnectorLogo";
import { POLYMARKET_ICON_URL } from "@/lib/polymarket-constants";
import { Button } from "@/components/ui/button";
interface WalletSelectorProps {
  value?: string; // Selected wallet address
  walletId?: string; // Fallback: wallet ID to find address if value is missing
  onChange: (walletId: string, walletAddress: string) => void;
  label?: string;
  description?: string;
  required?: boolean;
  /** When false, skip auto-selecting the default wallet on mount.
   *  Use in contexts where the user must choose explicitly (e.g., template fork modal).
   *  @default true */
  autoSelectDefault?: boolean;
  /** Filter wallets by chain family. "solana" shows only Solana wallets, "evm" shows only EVM. */
  chainFamily?: ChainFamily;
  /** Filter wallets by account type (e.g., "polymarket_deposit_wallet"). */
  accountType?: string;
}

/**
 * WalletSelector - Choose a Turnkey wallet for workflow actions
 *
 * NEW ARCHITECTURE (organization-centric):
 * - Uses organization ID from cookie
 * - Fetches wallets from b3os-workflow API
 * - Passes wallet ID (not subOrgId) to onChange callback
 */
function WalletSelector(t0) {
  const $ = _c(77);
  const {
    value,
    walletId,
    onChange,
    label,
    description,
    required,
    autoSelectDefault: t1,
    chainFamily,
    accountType
  } = t0;
  const autoSelectDefault = t1 === undefined ? true : t1;
  const requiredTokens = useRequiredTokens();
  const [showImportModal, setShowImportModal] = useState(false);
  const loadLegacyWallets = useWalletsStore(_temp);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = Cookies.get(B3OS_ORG_COOKIE_NAME);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const organizationId = t2;
  const {
    wallets: orgWallets,
    isLoading: orgLoading,
    refetch: refetchWallets
  } = useOrganizationWallets(organizationId);
  let t3;
  if ($[1] !== orgWallets) {
    t3 = orgWallets.find(_temp2)?.turnkeySubOrgId;
    $[1] = orgWallets;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const turnkeySubOrgId = t3;
  let t4;
  if ($[3] !== orgWallets) {
    t4 = orgWallets.find(_temp3)?.turnkeyRootUserId;
    $[3] = orgWallets;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const turnkeyRootUserId = t4;
  const canImport = Boolean(turnkeySubOrgId && turnkeyRootUserId);
  let t5;
  if ($[5] !== value) {
    t5 = {
      autoSelectFirst: false,
      preSelectedAddress: value,
      enabled: false
    };
    $[5] = value;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    isLoading: legacyLoading,
    setSelectedWallet
  } = useTurnkeyWallets(t5);
  const isLoading = orgLoading || legacyLoading;
  let t6;
  bb0: {
    let filtered = orgWallets;
    if (accountType) {
      let t7;
      if ($[7] !== accountType || $[8] !== filtered) {
        let t8;
        if ($[10] !== accountType) {
          t8 = w_1 => w_1.accountType === accountType;
          $[10] = accountType;
          $[11] = t8;
        } else {
          t8 = $[11];
        }
        t7 = filtered.filter(t8);
        $[7] = accountType;
        $[8] = filtered;
        $[9] = t7;
      } else {
        t7 = $[9];
      }
      filtered = t7;
    }
    let t7;
    if ($[12] !== chainFamily || $[13] !== filtered) {
      const all = filtered.map(_temp4);
      if (!chainFamily) {
        t6 = all;
        break bb0;
      }
      let t8;
      if ($[16] !== chainFamily) {
        t8 = w_3 => getChainFamily(w_3.address) === chainFamily;
        $[16] = chainFamily;
        $[17] = t8;
      } else {
        t8 = $[17];
      }
      t7 = all.filter(t8);
      $[12] = chainFamily;
      $[13] = filtered;
      $[14] = t7;
      $[15] = t6;
    } else {
      t7 = $[14];
      t6 = $[15];
    }
    t6 = t7;
  }
  const wallets = t6;
  let t7;
  if ($[18] !== orgWallets || $[19] !== value || $[20] !== walletId) {
    t7 = value || (walletId ? orgWallets.find(w_4 => w_4.id === walletId || w_4.address.toLowerCase() === walletId.toLowerCase())?.address : undefined);
    $[18] = orgWallets;
    $[19] = value;
    $[20] = walletId;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const selectedAddress = t7;
  const hasAutoSelectedRef = useRef(false);
  let t8;
  let t9;
  if ($[22] !== autoSelectDefault || $[23] !== isLoading || $[24] !== onChange || $[25] !== orgWallets || $[26] !== required || $[27] !== selectedAddress || $[28] !== wallets) {
    t8 = () => {
      if (autoSelectDefault && required && !selectedAddress && !isLoading && wallets.length > 0 && !hasAutoSelectedRef.current) {
        hasAutoSelectedRef.current = true;
        const defaultWallet = wallets.find(w_5 => orgWallets.find(o => o.address === w_5.address)?.isDefault) || wallets[0];
        if (defaultWallet) {
          onChange(defaultWallet.walletId, defaultWallet.address);
        }
      }
    };
    t9 = [autoSelectDefault, required, selectedAddress, isLoading, wallets, orgWallets, onChange];
    $[22] = autoSelectDefault;
    $[23] = isLoading;
    $[24] = onChange;
    $[25] = orgWallets;
    $[26] = required;
    $[27] = selectedAddress;
    $[28] = wallets;
    $[29] = t8;
    $[30] = t9;
  } else {
    t8 = $[29];
    t9 = $[30];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[31] !== value || $[32] !== walletId) {
    t10 = () => {
      if (value || walletId) {
        hasAutoSelectedRef.current = false;
      }
    };
    t11 = [value, walletId];
    $[31] = value;
    $[32] = walletId;
    $[33] = t10;
    $[34] = t11;
  } else {
    t10 = $[33];
    t11 = $[34];
  }
  useEffect(t10, t11);
  let t12;
  if ($[35] !== onChange || $[36] !== orgWallets || $[37] !== setSelectedWallet) {
    t12 = address => {
      const selectedWallet = orgWallets.find(w_6 => w_6.address === address);
      if (selectedWallet) {
        onChange(selectedWallet.id, selectedWallet.address);
      }
      setSelectedWallet(address);
    };
    $[35] = onChange;
    $[36] = orgWallets;
    $[37] = setSelectedWallet;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const handleSelectWallet = t12;
  let t13;
  if ($[39] !== loadLegacyWallets || $[40] !== onChange || $[41] !== refetchWallets || $[42] !== setSelectedWallet) {
    t13 = async filterAccountType => {
      const {
        data: refreshed
      } = await refetchWallets();
      loadLegacyWallets();
      let candidates = refreshed;
      if (filterAccountType) {
        candidates = candidates?.filter(w_7 => w_7.accountType === filterAccountType);
      }
      const newest = candidates?.toSorted(_temp5)?.[0];
      if (newest) {
        onChange(newest.id, newest.address);
        setSelectedWallet(newest.address);
      }
    };
    $[39] = loadLegacyWallets;
    $[40] = onChange;
    $[41] = refetchWallets;
    $[42] = setSelectedWallet;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  const refetchAndSelectNewest = t13;
  let t14;
  if ($[44] !== refetchAndSelectNewest) {
    t14 = async () => {
      try {
        await refetchAndSelectNewest();
      } catch {}
    };
    $[44] = refetchAndSelectNewest;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  const handleImportSuccess = t14;
  const isDepositWalletFilter = accountType === ACCOUNT_TYPE_POLYMARKET_DEPOSIT_WALLET;
  const [showSetupModal, setShowSetupModal] = useState(false);
  let t15;
  if ($[46] !== label || $[47] !== required) {
    t15 = label && <label className="field-label block">{label}{required && <span className="ml-1 text-red-500">*</span>}</label>;
    $[46] = label;
    $[47] = required;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== description) {
    t16 = description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>;
    $[49] = description;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== canImport || $[52] !== handleSelectWallet || $[53] !== isDepositWalletFilter || $[54] !== isLoading || $[55] !== requiredTokens || $[56] !== selectedAddress || $[57] !== wallets) {
    t17 = isDepositWalletFilter && wallets.length === 0 && !isLoading ? <div className="border-ds-border-default bg-ds-surface-card flex items-center gap-4 rounded-lg border p-4"><div className="flex-shrink-0"><ConnectorLogo type="polymarket" name="Polymarket" logoUrl={POLYMARKET_ICON_URL} size="md" /></div><div className="min-w-0 flex-1"><p className="body-sm-semibold text-ds-text-primary">Polymarket Deposit Wallet</p><p className="label-xs text-ds-text-muted">Required to trade on Polymarket</p></div><Button variant="brand" size="sm" onClick={() => setShowSetupModal(true)}>Set up</Button></div> : <WalletListSelector wallets={wallets} selectedAddress={selectedAddress} onSelect={handleSelectWallet} isLoading={isLoading} variant="default" showCreateLink={!isDepositWalletFilter} emptyStateMessage="No Turnkey wallets found. Please create a wallet in the" requiredTokens={requiredTokens} onImportWallet={!isDepositWalletFilter && canImport ? () => setShowImportModal(true) : undefined} />;
    $[51] = canImport;
    $[52] = handleSelectWallet;
    $[53] = isDepositWalletFilter;
    $[54] = isLoading;
    $[55] = requiredTokens;
    $[56] = selectedAddress;
    $[57] = wallets;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  let t18;
  if ($[59] !== canImport || $[60] !== handleImportSuccess || $[61] !== showImportModal || $[62] !== turnkeyRootUserId || $[63] !== turnkeySubOrgId) {
    t18 = canImport && <ImportPrivateKeyModal open={showImportModal} onOpenChange={setShowImportModal} organizationId={organizationId ?? ""} subOrgId={turnkeySubOrgId} rootUserId={turnkeyRootUserId} onSuccess={handleImportSuccess} />;
    $[59] = canImport;
    $[60] = handleImportSuccess;
    $[61] = showImportModal;
    $[62] = turnkeyRootUserId;
    $[63] = turnkeySubOrgId;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== accountType || $[66] !== isDepositWalletFilter || $[67] !== orgWallets || $[68] !== refetchAndSelectNewest || $[69] !== showSetupModal) {
    t19 = isDepositWalletFilter && <PolymarketDepositWalletSetupModal open={showSetupModal} onOpenChange={setShowSetupModal} wallets={orgWallets} onSetupCompleted={async () => {
      setShowSetupModal(false);
      try {
        await refetchAndSelectNewest(accountType);
      } catch {}
    }} />;
    $[65] = accountType;
    $[66] = isDepositWalletFilter;
    $[67] = orgWallets;
    $[68] = refetchAndSelectNewest;
    $[69] = showSetupModal;
    $[70] = t19;
  } else {
    t19 = $[70];
  }
  let t20;
  if ($[71] !== t15 || $[72] !== t16 || $[73] !== t17 || $[74] !== t18 || $[75] !== t19) {
    t20 = <div className="space-y-2">{t15}{t16}{t17}{t18}{t19}</div>;
    $[71] = t15;
    $[72] = t16;
    $[73] = t17;
    $[74] = t18;
    $[75] = t19;
    $[76] = t20;
  } else {
    t20 = $[76];
  }
  return t20;
}
function _temp5(a, b) {
  return b.createdAt - a.createdAt;
}
function _temp4(w_2) {
  return {
    address: w_2.address,
    walletId: w_2.id,
    walletName: w_2.name,
    isImported: w_2.isImported
  };
}
function _temp3(w_0) {
  return w_0.turnkeyRootUserId;
}
function _temp2(w) {
  return w.turnkeySubOrgId;
}
function _temp(state) {
  return state.loadWallets;
}
export default memo(WalletSelector, (prevProps, nextProps) =>
// Only re-render if value, walletId, label, description, or required actually changed
prevProps.value === nextProps.value && prevProps.walletId === nextProps.walletId && prevProps.label === nextProps.label && prevProps.description === nextProps.description && prevProps.required === nextProps.required && prevProps.chainFamily === nextProps.chainFamily && prevProps.accountType === nextProps.accountType
// Deliberately exclude onChange from comparison to prevent re-renders from callback changes
);